import React, { FC } from 'react';
import styled from '@emotion/styled';

import 'react-calendar/dist/Calendar.css';

import { useSelector, useI18n, useStaffCan, useButton } from '~Hooks';
import { useMetadata } from '~Hooks/useMetadata';
import { handyBreakpoint } from '~Styles/breakpoints';

import { useReportsApi } from '~Components/ReportsUniverse';
import { useDispatch } from '~Hooks';
import { useCashDrawer } from '~Providers/CashDrawerProvider';
import HistoryIcon from '~Assets/icons/history.svg';
import OpenInBrowserIcon from '~Assets/icons/open_in_browser.svg';
import * as actions from '~actions';

import Icon from '~ComponentsImproved/Icon';
import Heading from '~ComponentsImproved/Typography/Heading';
import PayoutHistory from './Components/PayoutHistory';
import LoginModal from '~Scenes/Tables/Components/LoginModal';
import BackTopBar from '~ComponentsImproved/BackTopBar';
import { BlueButton, Box, GrayButton } from '~Styles';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import CashRegisterBreakdown from './Components/CashRegisterBreakdown';
import CashSourceManager from './Components/CashSourceManager';

const Wrapper = styled(Box)<{ isHandy?: boolean }>`
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
  width: 100%;
  ${({ isHandy }) =>
    isHandy ? `position: relative; height: auto;` : `position: fixed; height: 100%;`}
`;

const ScrollWrapper = styled(Box)<{ isHandy?: boolean }>`
  padding: 20px 40px 20px 20px;
  box-sizing: border-box;
  opacity: 0;
  animation: fadeIn 0.2s ease-in-out 0.1s forwards;

  /* POS scrolls inside this box; Handy lets the outer MainContent scroll. */
  ${({ isHandy }) =>
    isHandy
      ? ``
      : `
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  `}

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const OpenCashDrawerButton = styled(BlueButton)`
  flex: 1;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
`;

// Arrow points down: rotate the open/external icon 180°.
const OpenDrawerIcon = styled.span`
  display: inline-flex;
  align-items: center;
  transform: rotate(180deg);
`;

const OpenHistoryButton = styled(GrayButton)`
  height: 52px;
  width: 52px;
  min-width: 52px;
  padding: 0;
`;

const ActionRow = styled(Box)`
  gap: 10px;
`;

const HandyHeader = styled(Box)`
  margin-bottom: 8px;
`;

// On wide (POS) screens the breakdown and the cash sources sit side by side.
// On narrow (Handy) stack vertically so neither gets squashed.
const BreakdownRow = styled(Box)`
  ${handyBreakpoint} {
    flex-direction: column;
    align-items: stretch;
  }
`;

interface Props {
  isHandy?: boolean;
  // Handy-supplied back handler, wired into BackTopBar (react-router back won't work in Handy).
  onBack?: () => void;
}

const CashSources: FC<Props> = ({ isHandy = false, onBack }) => {
  const { i18n } = useI18n();
  const staffCan = useStaffCan();
  const reportsApi = useReportsApi();
  const cashDrawer = useCashDrawer();

  const staffs = useSelector((state) => state.companyStaff?.staffs ?? []);
  const staffId = useSelector((state) => state.staff?.id);
  const hasStaff = staffs.length > 0;

  const historyModal = useModal();

  const dispatch = useDispatch();

  const language = useSelector((state) => state.language);

  let lang: string = language;
  if (language == 'se') lang = 'sv';

  const metadata = useMetadata();

  const openHistoryButton = useButton({
    onClick: () => historyModal.show(),
    disabled: hasStaff && !staffId,
  });

  const openCashDrawerButton = useButton({
    onClick: () => {
      dispatch(actions.addToast(i18n('Cash.DrawerOpened'), true));
      reportsApi.cashBoxOpened();
      cashDrawer.open();
    },
    disabled: !staffCan.CLOSE_DAY,
    disabledToast: i18n('Cash.DrawerOpen.PermissionDenied'),
  });
  // Shared action buttons reused in both the POS top bar and the Handy in-page header.
  const actionButtons = (
    <>
      <OpenCashDrawerButton size="large" {...openCashDrawerButton}>
        <OpenDrawerIcon>
          <Icon
            src={OpenInBrowserIcon}
            color="var(--colorBlueGray100)"
            width="24px"
            height="24px"
          />
        </OpenDrawerIcon>
        {i18n('CashManagement.OpenCashDrawer')}
      </OpenCashDrawerButton>

      <OpenHistoryButton
        size="small"
        display="flex"
        alignItems="center"
        justifyContent="center"
        {...openHistoryButton}>
        <Icon src={HistoryIcon} color="var(--colorBlueGray100)" width="24px" height="24px" />
      </OpenHistoryButton>
    </>
  );

  return (
    <>
      <Wrapper isHandy={isHandy} display="flex" flexDirection="column">
        {!isHandy && (
          <BackTopBar title={i18n('Menu.CashManagement')} dark goBack={onBack}>
            <Box display="flex" alignItems="center" gap="xs" marginLeft="xs">
              {actionButtons}
            </Box>
          </BackTopBar>
        )}

        <ScrollWrapper
          isHandy={isHandy}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="large">
          {/* Handy: title + actions scroll with the page instead of being pinned. */}
          {isHandy && (
            <HandyHeader fluid display="flex" flexDirection="column" gap="xs">
              <Heading level={2} color="var(--colorWhite)">
                {i18n('Menu.CashManagement')}
              </Heading>

              <ActionRow fluid display="flex" alignItems="center">
                {actionButtons}
              </ActionRow>
            </HandyHeader>
          )}

          {staffCan.CLOSE_DAY && !metadata.loading && (
            <BreakdownRow
              fluid
              display="flex"
              justifyContent="center"
              gap={isHandy ? 'xs' : 'xl'}
              marginTop="medium">
              <CashRegisterBreakdown cashFloat={metadata.data.cashFloat} />
              <CashSourceManager cashSources={metadata.data.cashSources} />
            </BreakdownRow>
          )}
        </ScrollWrapper>
      </Wrapper>

      {!isHandy && !staffId && <LoginModal />}

      <Modal isShowing={historyModal.isShowing} hide={historyModal.hide}>
        <PayoutHistory cashSources={metadata.data.cashSources} onClose={historyModal.hide} />
      </Modal>
    </>
  );
};

export default CashSources;
